import { Component, Input, OnInit, OnChanges, SimpleChanges, forwardRef, OnDestroy, ChangeDetectorRef } from '@angular/core';

import {
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
  Validator,
  NG_VALIDATORS
} from '@angular/forms';

// Export Validators to use in template
export { Validators };
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { Subject, take, takeUntil } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { RecruitmentUser as User } from '@models/domains/recruitment';
import { CategoriePoste } from '@models/domains/posts';
import { CaracteristiquesFormValue } from '@models/api/requests';
import { ReferentielsService } from 'src/app/core/services/referentiels.service';
import { DateTime } from 'luxon';
import { MAT_DATE_LOCALE, DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { CustomLuxonDateAdapter } from '../../utils/custom-luxon-date-adapter';
import { MY_FORMATS } from '../../utils/date-pickr-custom-luxon-format';
import { RecruitmentUtils } from '../../utils/recruitment.utils';

type CaracteristiquesControlName = 'etatPoste' | 'occupationPoste' | 'categoriePoste' | 'posteBudgete';
type ManualControlName = 'dateRecrutement' | 'dateDepart' | 'interne' | 'externe';

const VACATAIRE_LOCKED_CONTROL_STATES: Record<CaracteristiquesControlName, ControlStateConfig> = {
  etatPoste: { required: false, enabled: false, reset: true },
  occupationPoste: { required: false, enabled: false, reset: true },
  categoriePoste: { required: false, enabled: false, reset: true },
  posteBudgete: { required: true, enabled: false, reset: false }
};

const VACATAIRE_MANUAL_POSTE_BUDGETE_STATE: ControlStateConfig = { required: true, enabled: true, reset: false };
const MANUAL_FIELDS: ManualControlName[] = ['dateRecrutement', 'dateDepart', 'interne', 'externe'];

interface ControlStateConfig {
  required?: boolean;
  enabled?: boolean;
  reset?: boolean;
}

@Component({
    selector: 'app-caracteristiques-form',
    imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatIconModule,
    MatButtonModule,
    MatSelectModule,
    MatDatepickerModule,
    MatCheckboxModule
],
    templateUrl: './caracteristiques-form.component.html',
    styleUrls: ['./caracteristiques-form.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CaracteristiquesFormComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => CaracteristiquesFormComponent),
            multi: true
        },
        {
            provide: MAT_DATE_LOCALE,
            useValue: 'fr-FR'
        },
        { provide: DateAdapter, useClass: CustomLuxonDateAdapter, deps: [MAT_DATE_LOCALE] },
        { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS }
    ]
})
export class CaracteristiquesFormComponent implements OnInit, OnChanges, ControlValueAccessor, OnDestroy, Validator {
  // Make Validators available in template
  public Validators = Validators;

  @Input() readOnly = false;
  @Input() typeRecrutement: any = null;

  private _posteId: string | null = null;
  private _isInitialLoad = true;
  @Input() set posteId(value: string | null) {
    this._posteId = value;
    if (value) {
      // Load position data - pass flag to distinguish initial load vs position change
      this.loadDetenteurPoste(value, this._isInitialLoad);
      this._isInitialLoad = false; // After first load, all subsequent calls are position changes
    } else {
      // Reset form values when poste is cleared
      this.form.patchValue({
        etatPoste: '',
        occupationPoste: '',
        categoriePoste: null,
        posteBudgete: null,
        detenteurPoste: null
      });
      this._agentsList = [];
      
      // Apply workflow rules when post is cleared - this is critical for vacataire field enabling
      if (!this.readOnly) {
        this.applyPostSelectedFieldState();
      }
    }
  }
  get posteId(): string | null {
    return this._posteId;
  }

  // Private properties for internal use
  private _agentsList: User[] = [];
  private _categoriePosteList: CategoriePoste[] = [];

  // Getters for template access
  get categoriePosteList(): CategoriePoste[] {
    return this._categoriePosteList;
  }

  form: FormGroup;
  minDateRecrutement: Date | null;
  dateRecrutementInitiale: Date | null;
  minDateDepart: Date | null;
  dateDepartInitiale: Date | null;
  minDateJour: Date = new Date();

  private readonly destroy$ = new Subject<void>();
  private onChange: (value: CaracteristiquesFormValue) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidationChange: () => void = () => {};

  constructor(
    private readonly fb: FormBuilder,
    private readonly referentielsService: ReferentielsService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.form = this.fb.group(
      {
        etatPoste: [null, Validators.required],
        dateRecrutement: [null, Validators.required],
        occupationPoste: [{ value: null, disabled: true }, Validators.required],
        detenteurPoste: [{ value: null, disabled: true }],
        categoriePoste: [{ value: null, disabled: true }],
        posteBudgete: [{ value: null, disabled: true }, Validators.required],
        dateDepart: [null],
        interne: [false],
        externe: [false]
      },
      {
        validators: this.atLeastOneCheckboxSelected()
      }
    );
  }
  validate(_control: AbstractControl): ValidationErrors | null {
    return this.form.valid ? null : { invalidCaracteristiquesData: { invalidControls: this.getInvalidControls() } };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['typeRecrutement'] && !this.readOnly) {
      // Apply workflow rules when typeRecrutement changes
      this.applyPostSelectedFieldState(this.typeRecrutement);
    }
  }

  ngOnInit(): void {
    this.form.get('dateRecrutement')?.valueChanges.subscribe((value) => {
      if (typeof value === 'string' && value.length === 10) {
        const dateValue = DateTime.fromFormat(value, 'dd/MM/yyyy');
        if (dateValue.isValid) {
          this.form.get('dateRecrutement')?.setValue(dateValue.toJSDate(), { emitEvent: false });
          this.form.get('dateRecrutement')?.setErrors(null);
        } else {
          // this.form.get('dateRecrutement')?.setErrors({ invalidDate: true });
        }
      }
    });

    this.form.get('dateDepart')?.valueChanges.subscribe((value) => {
      if (typeof value === 'string' && value.length === 10) {
        const dateValue = DateTime.fromFormat(value, 'dd/MM/yyyy');
        if (dateValue.isValid) {
          this.form.get('dateDepart')?.setValue(dateValue.toJSDate(), { emitEvent: false });
          this.form.get('dateDepart')?.setErrors(null);
        } else {
          // this.form.get('dateDepart')?.setErrors({ invalidDate: true });
        }
      }
    });
    // Date validation handled by date pickers

    // Subscribe to form value changes to notify parent
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.onChange(this.form.getRawValue());
      this.onTouched();
    });

    // Subscribe to form status changes to notify parent of validation changes
    this.form.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.onValidationChange();
    });

    // Load reference data
    this.loadCategoriePosteList();
  }

  // Method to validate the entire form
  validateForm(): boolean {
    this.form.markAllAsTouched();
    return this.form.valid;
  }

  // ControlValueAccessor methods
  writeValue(value: CaracteristiquesFormValue): void {
    if (value) {
      // Patch all fields from value, handling detenteurPoste specially
      const formData: any = {
        etatPoste: value.etatPoste,
        dateRecrutement: value.dateRecrutement,
        occupationPoste: value.occupationPoste,
        categoriePoste: value.categoriePoste,
        posteBudgete: value.posteBudgete,
        dateDepart: value.dateDepart,
        interne: value.interne,
        externe: value.externe
      };
      
      // Include detenteurPoste only if it's string or null (skip objects - they come from API)
      const detenteurValue = value.detenteurPoste;
      if (typeof detenteurValue === 'string' || detenteurValue === null) {
        formData.detenteurPoste = detenteurValue;
      }
      
      this.form.patchValue(formData, { emitEvent: true });
      
      // Update validation immediately
      this.form.updateValueAndValidity();
      this.onValidationChange();
      
      this.dateRecrutementInitiale = value.dateRecrutement ? new Date(value.dateRecrutement) : null;
      this.dateDepartInitiale = value.dateDepart ? new Date(value.dateDepart) : null;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidationChange = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.readOnly || isDisabled) {
      // In readonly mode or when explicitly disabled, disable everything
      this.form.disable({ emitEvent: false });
    } else {
      // Apply workflow rules which will enable/disable fields individually
      this.applyPostSelectedFieldState();
    }
  }

  private updateDetenteurPosteFieldState(): void {
    const hasApiDetenteurData = this._agentsList && this._agentsList.length > 0;
    const detenteurControl = this.form.controls['detenteurPoste'];
    
    if (hasApiDetenteurData) {
      // If we have agent data from API, disable field
      detenteurControl.disable({ emitEvent: false });
      // Remove validators for disabled field
      detenteurControl.clearValidators();
    } else {
      // If no agent data, allow manual input
      detenteurControl.enable({ emitEvent: false });
      // detenteurPoste is never required per business rules
      detenteurControl.clearValidators();
    }
    
    detenteurControl.updateValueAndValidity();
  }

  /**
   * Apply workflow rule F: Disable specific API-controlled fields when post is selected
   * Disables: categoriePoste, occupationPoste, posteBudgete
   * Keeps enabled: etatPoste, dateRecrutement, dateDepart, interne, externe, detenteurPoste
   */
  applyPostSelectedFieldState(typeRecrutement?: any): void {
    // Use provided parameter, or fall back to @Input() typeRecrutement
    const recruitmentType = typeRecrutement || this.typeRecrutement;
    const isVacataire = RecruitmentUtils.isVacataireType(recruitmentType);
    const isPostSelected = !!this._posteId;

    if (isVacataire) {
      (Object.entries(VACATAIRE_LOCKED_CONTROL_STATES) as [CaracteristiquesControlName, ControlStateConfig][]).forEach(
        ([field, config]) => this.applyControlState(field, config)
      );

      if (!isPostSelected) {
        this.applyControlState('posteBudgete', VACATAIRE_MANUAL_POSTE_BUDGETE_STATE);
      }

      this.enableManualFields();
      this.updateDetenteurPosteFieldState();
      return;
    }

    const workflowStates: Record<CaracteristiquesControlName, ControlStateConfig> = {
      etatPoste: { required: true, enabled: true },
      occupationPoste: { required: true, enabled: false },
      categoriePoste: { required: false, enabled: false },
      posteBudgete: { required: true, enabled: false }
    };

    (Object.entries(workflowStates) as [CaracteristiquesControlName, ControlStateConfig][]).forEach(([field, config]) => {
      this.applyControlState(field, config);
    });

    this.enableManualFields();
    this.updateDetenteurPosteFieldState();
  }

  private enableManualFields(): void {
    MANUAL_FIELDS.forEach((name) => {
      this.form.get(name)?.enable({ emitEvent: false });
    });
  }

  private applyControlState(name: CaracteristiquesControlName, config: ControlStateConfig): void {
    const control = this.form.get(name);

    if (!control) {
      return;
    }

    if (config.reset) {
      control.setValue(null, { emitEvent: false });
      control.setErrors(null);
    }

    if (config.required === true) {
      control.setValidators(Validators.required);
    } else if (config.required === false) {
      control.clearValidators();
    }

    if (config.enabled === true) {
      control.enable({ emitEvent: false });
    } else if (config.enabled === false) {
      control.disable({ emitEvent: false });
    }

    control.updateValueAndValidity({ emitEvent: false });
  }

  // Date validation
  verifDateSaisie = () => {
    return (control: AbstractControl): null | ValidationErrors => {
      const dateDepart = this.form.get('dateDepart')?.value;
      const dateRecrutement = this.form.get('dateRecrutement')?.value;

      if (!dateDepart || !dateRecrutement) {
        return null;
      }

      const dateDebut = DateTime.fromJSDate(new Date(dateDepart)).startOf('day');
      const dateFin = DateTime.fromJSDate(new Date(dateRecrutement)).startOf('day');

      if (dateDebut <= dateFin) {
        return { erreurDateDebutValid: control.value, erreurDateValidation: true };
      }
      return null;
    };
  };

  // vérifications date de recrutement
  onChangeRecrutementdate() {
    let dateDepart = this.form.get('dateDepart')?.value;
    dateDepart = DateTime.fromJSDate(new Date(dateDepart));
    let dateRecrutement = this.form.get('dateRecrutement')?.value;
    dateRecrutement = DateTime.fromJSDate(new Date(dateRecrutement));
    let dateMin = DateTime.fromJSDate(new Date()).startOf('day');

    if (dateRecrutement.isValid && dateDepart.isValid) {
      if (dateRecrutement > dateDepart) {
        this.form.get('dateRecrutement')?.setErrors({ dateR_Invalide: true });
      } else if (dateRecrutement < dateMin) {
        this.form.get('dateRecrutement')?.setErrors({ matDatepickerMin: true });
      } else {
        this.form.get('dateRecrutement')?.setErrors(null);
        this.form.get('dateDepart')?.setErrors(null);
        this.form.get('dateRecrutement')?.updateValueAndValidity();
        this.form.get('dateDepart')?.updateValueAndValidity();
      }
    }
  }

  // vérifications date de départ
  onChangeDepartdate() {
    let dateDepart = this.form.get('dateDepart')?.value;
    dateDepart = DateTime.fromJSDate(new Date(dateDepart));
    let dateRecrutement = this.form.get('dateRecrutement')?.value;
    dateRecrutement = DateTime.fromJSDate(new Date(dateRecrutement));
    let dateMin = DateTime.fromJSDate(new Date()).startOf('day');

    if (dateRecrutement.isValid && dateDepart.isValid) {
      if (dateRecrutement > dateDepart) {
        this.form.get('dateDepart')?.setErrors({ dateD_Invalide: true });
      } else if (dateDepart < dateMin) {
        this.form.get('dateDepart')?.setErrors({ matDatepickerMin: true });
      } else {
        this.form.get('dateDepart')?.setErrors(null);
        this.form.get('dateRecrutement')?.setErrors(null);
        this.form.get('dateRecrutement')?.updateValueAndValidity();
        this.form.get('dateDepart')?.updateValueAndValidity();
      }
    }
  }

  // calcule la date min du calendrier
  datePickerOpened(champDate: string) {
    if (champDate == 'dateRecrutement') {
      this.minDateRecrutement = this.minDateJour;
    } else if (champDate == 'dateDepart') {
      this.minDateDepart = this.minDateJour;
    }
  }


  // Helper methods
  idComparer(o1: any, o2: any): boolean {
    return o1 && o2 ? o1.id === o2.id : o1 === o2;
  }


  // Load detenteur poste data
  loadDetenteurPoste(idPoste: string, isInitialLoad = false) {
    if (!idPoste) {
      console.warn('Attempted to load detenteur with null or empty idPoste');
      return;
    }

    this.referentielsService
      .getDetenteurPoste(idPoste)
      .pipe(
        take(1),
        finalize(() => {
          // Schedule validation in microtask queue after observable completes
          this.form.updateValueAndValidity();
          this.onValidationChange();
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (data: any) => {
          if (data) {
            // ✅ Handle detenteurPoste display properly
            let detenteurDisplay = null;
            const hasApiDetenteurData = data.detenteurs?.length > 0;
            
            if (hasApiDetenteurData) {
              const detenteur = data.detenteurs[0];
              detenteurDisplay = typeof detenteur === 'object' 
                ? `${detenteur.nom} ${detenteur.prenom}` 
                : detenteur;
            }
            
            // Prepare patch data - always update API-controlled fields
            const patchData: any = {
              etatPoste: data.etat || '',
              occupationPoste: data.occupation || '',
              categoriePoste: data.categoriePoste || null,
              posteBudgete: data.budgete !== undefined ? data.budgete : null
            };
            
            // Handle detenteurPoste based on scenario
            if (isInitialLoad) {
              // Initial load: preserve detenteurNom values, otherwise use API data
              const currentValue = this.form.get('detenteurPoste')?.value;
              const hasDetenteurNomValue = typeof currentValue === 'string' && currentValue.trim();
              
              if (hasDetenteurNomValue && !hasApiDetenteurData) {
                // Preserve existing detenteurNom value when no API data on initial load
              } else {
                // Use API data or clear field
                patchData.detenteurPoste = hasApiDetenteurData ? detenteurDisplay : '';
              }
            } else {
              // Position change: always clear and use new position's data
              patchData.detenteurPoste = hasApiDetenteurData ? detenteurDisplay : '';
            }
            
            this.form.patchValue(patchData);
            this._agentsList = data.detenteurs || [];
            
            // ✅ Apply workflow rules for field states after loading post data
            this.applyPostSelectedFieldState();
            
            this.form.markAllAsTouched();
          }
        },
        error: (error: any) => {
          console.error('Error loading detenteur poste:', error);
          this._agentsList = [];
        }
      });
  }

  // Load reference data
  loadCategoriePosteList() {
    this.referentielsService
      .getCategoriePoste()
      .pipe(take(1))
      .subscribe((data) => {
        if (data) {
          this._categoriePosteList = data.filter((o: CategoriePoste) => o.libelle && o.libelle.trim() !== '');
          this.cdr.detectChanges();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Validator to ensure at least one checkbox is selected
  private atLeastOneCheckboxSelected(): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const interne = formGroup.get('interne')?.value;
      const externe = formGroup.get('externe')?.value;

      return interne || externe ? null : { atLeastOneRequired: true };
    };
  }

  public getInvalidControls(): string[] {
    const invalidControls: string[] = [];
    const controls = this.form.controls;
    for (const name in controls) {
      if (controls[name].invalid) {
        invalidControls.push(`CaracteristiquesForm: ${name}`);
      }
    }
    return invalidControls;
  }
}
